/**
 * Formatting and validation helpers for the Venus Star Point order form.
 *
 * Natal birth-moment contract: the birth date and time travel as civil strings
 * ('YYYY-MM-DD', 'HH:MM') from the moment they are entered. Nothing here builds
 * a birth instant from a Date with device-local getters; the server resolves the
 * timezone and UTC, and the client only displays what the server returns.
 */
import { getUTCOffset } from '../../utils/location';

const HOUR_MS = 3600000;

export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

export const MIN_BIRTH_YMD = '1900-01-01';

const YMD_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const HM_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;

const pad2 = (n) => String(n).padStart(2, '0');

/** Today's date in the buyer's own timezone, as 'YYYY-MM-DD' (the max birth date). */
export function todayYmd() {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

/**
 * react-datepicker builds its Dates from local calendar components (local
 * midnight of the clicked day), so reading the same local components back is
 * the exact inverse and yields the civil date the buyer picked. This is the
 * only place a Date touches the birth date, and only at the picker boundary.
 */
export function pickerDateToYmd(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

/** Inverse of pickerDateToYmd, for feeding a stored civil date back to the picker. */
export function ymdToPickerDate(ymd) {
  const m = YMD_RE.exec(ymd || '');
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
}

/** A real calendar date between 1900-01-01 and today (buyer's local date). */
export function isValidBirthYmd(ymd) {
  const m = YMD_RE.exec(ymd || '');
  if (!m) return false;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const probe = new Date(Date.UTC(y, mo - 1, d));
  if (probe.getUTCFullYear() !== y || probe.getUTCMonth() !== mo - 1 || probe.getUTCDate() !== d) return false;
  return ymd >= MIN_BIRTH_YMD && ymd <= todayYmd();
}

/** 'YYYY-MM-DD' minus n years by string arithmetic (29 Feb → 28 Feb when the year has none). */
export function minusYearsYmd(ymd, n) {
  const m = YMD_RE.exec(ymd || '');
  if (!m) return '';
  const y = Number(m[1]) - n;
  const leap = (y % 4 === 0 && y % 100 !== 0) || y % 400 === 0;
  const d = m[2] === '02' && m[3] === '29' && !leap ? '28' : m[3];
  return `${String(y).padStart(4, '0')}-${m[2]}-${d}`;
}

/** The latest birth date that is minAge years old today (the date picker's maximum). */
export const latestBirthYmd = (minAge) => (minAge ? minusYearsYmd(todayYmd(), minAge) : todayYmd());

/** Old enough on today's date? (The server checks again on the order date.) */
export const isOldEnough = (ymd, minAge) => !minAge || (!!ymd && ymd <= latestBirthYmd(minAge));

export function isValidTime(hm) {
  return HM_RE.test(hm || '');
}

/** '1981-05-31' -> '31 May 1981' */
export function formatCivilDate(ymd) {
  const m = YMD_RE.exec(ymd || '');
  if (!m) return '';
  return `${Number(m[3])} ${MONTHS[Number(m[2]) - 1]} ${m[1]}`;
}

/**
 * Split a server UTC ISO string into display parts without device-local getters.
 * '1981-05-31T13:30:00Z' -> { ymd: '1981-05-31', date: '31 May 1981', time: '13:30' }
 */
export function splitUtcIso(iso) {
  if (typeof iso !== 'string') return null;
  const m = /^(\d{4}-\d{2}-\d{2})T(\d{2}):(\d{2})/.exec(iso);
  if (m && /(Z|[+-]00:?00)$/i.test(iso.trim())) {
    return { ymd: m[1], date: formatCivilDate(m[1]), time: `${m[2]}:${m[3]}` };
  }
  return utcMsToParts(new Date(iso).getTime());
}

/** Epoch ms -> UTC display parts (UTC getters only, so device-independent). */
function utcMsToParts(ms) {
  if (!Number.isFinite(ms)) return null;
  const d = new Date(ms);
  const ymd = `${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`;
  return { ymd, date: formatCivilDate(ymd), time: `${pad2(d.getUTCHours())}:${pad2(d.getUTCMinutes())}` };
}

/** 44.8565, 24.8692 -> '44.86° N, 24.87° E' (S/W instead of negative numbers). */
export function formatLatLon(lat, lon) {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return '';
  const ns = lat >= 0 ? 'N' : 'S';
  const ew = lon >= 0 ? 'E' : 'W';
  return `${Math.abs(lat).toFixed(2)}° ${ns}, ${Math.abs(lon).toFixed(2)}° ${ew}`;
}

/** 'Pitești, Argeș, Romania' -> 'Pitești' */
export function shortPlace(label) {
  return String(label || '').split(',')[0].trim();
}

/** 3 -> 'UTC+3', -3.5 -> 'UTC−3:30', 0 -> 'UTC' */
export function formatOffsetHours(hours) {
  if (!Number.isFinite(hours)) return '';
  if (hours === 0) return 'UTC';
  const sign = hours > 0 ? '+' : '−';
  const totalMin = Math.round(Math.abs(hours) * 60);
  const h = Math.floor(totalMin / 60);
  const mi = totalMin % 60;
  return `UTC${sign}${h}${mi ? `:${pad2(mi)}` : ''}`;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export function isValidEmail(email) {
  const e = String(email || '').trim();
  return e.length > 0 && e.length <= 254 && EMAIL_RE.test(e);
}

export function normalizeEmail(email) {
  return String(email || '').trim().toLowerCase();
}

/** Name on the reading: 1-80 characters, control characters removed, NFC. */
export function cleanName(name) {
  // eslint-disable-next-line no-control-regex
  const stripped = String(name || '').replace(/[\u0000-\u001f\u007f-\u009f]/g, '').replace(/\s+/g, ' ').trim();
  return typeof stripped.normalize === 'function' ? stripped.normalize('NFC') : stripped;
}

export function isValidName(name) {
  const n = cleanName(name);
  return n.length >= 1 && n.length <= 80;
}

/**
 * Parse a manually typed UTC offset ('+2', '-05:00', '+0530', '−3:30') into the
 * '+HH:MM' form the server expects. Returns null when it is not a real offset.
 */
export function normalizeUtcOffset(input) {
  const s = String(input || '').trim().replace(/−/g, '-').replace(/^UTC\s*/i, '');
  const m = /^([+-])?(\d{1,2})(?::?([0-5]\d))?$/.exec(s);
  if (!m) return null;
  const sign = m[1] === '-' ? '-' : '+';
  const h = Number(m[2]);
  const mi = Number(m[3] || 0);
  const total = (h * 60 + mi) * (sign === '-' ? -1 : 1);
  if (total < -12 * 60 || total > 14 * 60) return null;
  return `${sign}${pad2(h)}:${pad2(mi)}`;
}

/** '+05:30' -> 5.5 */
export function offsetStringToHours(offset) {
  const m = /^([+-])(\d{2}):(\d{2})$/.exec(offset || '');
  if (!m) return null;
  return (m[1] === '-' ? -1 : 1) * (Number(m[2]) + Number(m[3]) / 60);
}

/**
 * Display-only helper for the "summer or winter time?" question: the two UTC
 * readings of a wall-clock time that happened twice in `tz` (clocks went back).
 * The server stays the source of truth; it recomputes from dstChoice.
 * Returns [{ choice:'earlier'|'later', offsetHours, utc:{date,time} }] or null.
 */
export function ambiguousCandidates(ymd, hm, tz) {
  const d = YMD_RE.exec(ymd || '');
  const t = HM_RE.exec(hm || '');
  if (!d || !t || !tz) return null;
  try {
    // Wall clock read as if it were UTC; real instants are this minus each offset.
    const wall = Date.UTC(Number(d[1]), Number(d[2]) - 1, Number(d[3]), Number(t[1]), Number(t[2]));
    const offsets = new Set([
      getUTCOffset(new Date(wall - 12 * HOUR_MS), tz),
      getUTCOffset(new Date(wall + 12 * HOUR_MS), tz),
    ]);
    const hits = [];
    offsets.forEach((off) => {
      const utcMs = wall - off * HOUR_MS;
      if (Math.abs(getUTCOffset(new Date(utcMs), tz) - off) < 1e-6) hits.push({ off, utcMs });
    });
    if (hits.length !== 2) return null;
    hits.sort((a, b) => a.utcMs - b.utcMs);
    return hits.map((h, i) => ({
      choice: i === 0 ? 'earlier' : 'later',
      offsetHours: h.off,
      utc: utcMsToParts(h.utcMs),
    }));
  } catch (e) {
    return null;
  }
}

/** All IANA zone names the browser knows, or null when the API is missing. */
export function listTimeZones() {
  try {
    if (typeof Intl !== 'undefined' && typeof Intl.supportedValuesOf === 'function') {
      return Intl.supportedValuesOf('timeZone');
    }
  } catch (e) {
    // fall through
  }
  return null;
}

/** Sanitized marketing source: src / utm_source from the URL, else null. src first: on an ad it names the campaign, utm_source only says "google". */
export function readSourceParam(search) {
  try {
    const params = new URLSearchParams(search || '');
    const raw = params.get('src') || params.get('utm_source') || '';
    const clean = raw.toLowerCase().replace(/[^a-z0-9_.-]/g, '').slice(0, 40);
    return clean || null;
  } catch (e) {
    return null;
  }
}
